import axios from 'axios';
import store from '../store';
import { PigRun } from '../models/PigRun';
import { WebPassage } from '../models/WebPassage';
import { CompletedPigRunBroadcastCommand } from '@/models/CompletedPigRunBroadcastCommand';
import { ReportDataForCompletedPigRunRequest } from '@/models/ReportManager/ReportDataForCompletedPigRunRequest';
import { ReportDataForDevicesRequest } from '@/models/ReportManager/ReportDataForDevicesRequest';
import { PassageVerificationRequest } from '@/models/ReportManager/PassageVerificationRequest';
import { DeleteManualPassageRequest } from '@/models/ReportManager/DeleteManualPassageRequest';
import { AddManualPassageRequest } from '@/models/ReportManager/AddManualPassageRequest';
import { BroadcastCommandsRequest } from '@/models/BroadcastCommands/BroadcastCommandsRequest';
import { UpdatePassageTimeStampRequest } from '@/models/ReportManager/UpdatePassageTimeStampRequest';
import { DownloadRecordedPassageDataRequest } from '@/models/ReportManager/DownloadRecordedPassageDataRequest';
import { DownloadRecordedRawDataRequest } from '@/models/DownloadRecordedRawData/DownloadRecordedRawDataRequest';
import { DownloadRecordedPassageDataFirmwareValidationRequest } from '@/models/ReportManager/DownloadRecordedPassageDataFirmwareValidationRequest';
import { UpdateA2PMessagingStateRequest } from '@/models/Notification/UpdateA2PMessagingStateRequest';
import { MachineLearningResultsRequest } from '@/models/MachineLearning/MachineLearningResultsRequest';
import { GetToolRequest } from '@/models/MachineLearning/GetToolRequest';
import { UpdatePigRunJobNumberRequest } from '@/models/UpdatePigRunJobNumberRequest';
import { AddPigRunToolRequest } from '@/models/AddPigRunToolRequest';
import { DeleteAllPigRunToolsForPigRunRequest } from '@/models/DeleteAllPigRunToolsForPigRunRequest';

import { AddPigRunRequest } from '@/models/AddPigRunRequest';
import { UpdatePigRunRequest } from '@/models/UpdatePigRunRequest';
import { ReconstructPigRunRequest } from '@/models/ReconstructPigRunRequest';
import { LaunchLiveTrackPigRunRequest } from '@/models/LaunchLiveTrackPigRunRequest';
import { UpdateMarkerTrackStateRequest } from '@/models/UpdateMarkerTrackStateRequest';
import { UpdateMarkerNoteRequest } from '@/models/UpdateMarkerNoteRequest';
import { UpdateMarkerDescriptionRequest } from '@/models/UpdateMarkerDescriptionRequest';
import { PigRunNotification } from '@/models/PigRunNotification';
import { AddDynamicMarkerRequest } from '@/models/AddDynamicMarkerRequest';
import { IMLPassageResult, IMLPassagesRequest } from '@/models/Passage';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT,
  withCredentials: false, // This is the default
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export default {
  // Web api service call used to create broadcast commands (main menu item call).
  async createBroadcastCommands(
    broadcastCommandsRequest: BroadcastCommandsRequest
  ) {
    return await apiClient.post(
      '/pigrun/CreateBroadcastCommands',
      broadcastCommandsRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to add a manual passage at a specific pipeline section marker.
  async addManualPassage(addManualPassageRequest: AddManualPassageRequest) {
    return await apiClient.post(
      '/pigrun/AddManualPassage',
      addManualPassageRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to delete a manual passage for the specified PigRun.RunId.
  async deleteManualPassage(
    deleteManualPassageRequest: DeleteManualPassageRequest
  ) {
    return await apiClient.post(
      '/pigrun/DeleteManualPassage',
      deleteManualPassageRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to remove a manual link device from a pipeline section marker.
  async removeManualLink(pipelineSectionMarkerId: number) {
    return await apiClient.post(
      '/pigrun/RemoveManualLink',
      pipelineSectionMarkerId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to add a manual link device to a pipeline section marker.
  async addManualLink(pipelineSectionMarkerId: number, uid: number) {
    return await apiClient.post(
      '/pigrun/AddManualLink',
      { pipelineSectionMarkerId, uid },
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to get report data for a completed pig run.
  async getReportDataForCompletedPigRun(
    reportDataForCompletedPigRunRequest: ReportDataForCompletedPigRunRequest
  ) {
    return await apiClient.post(
      '/pigrun/GetReportDataForCompletedPigRun',
      reportDataForCompletedPigRunRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to get report data for device(s).
  async getReportDataForDevices(
    reportDataForDevicesRequest: ReportDataForDevicesRequest
  ) {
    return await apiClient.post(
      '/pigrun/GetReportDataForDevices',
      reportDataForDevicesRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to create completed pig run broadcast commands.
  async createCompletedPigRunBroadcastCommands(
    completedPigRunBroadcastCommand: CompletedPigRunBroadcastCommand
  ) {
    return await apiClient.post(
      '/pigrun/CreateCompletedPigRunBroadcastCommands',
      completedPigRunBroadcastCommand,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to set passage verification.
  async setPassageWaveformVerification(
    passageVerificationRequest: PassageVerificationRequest
  ) {
    return await apiClient.post(
      'pigrun/SetPassageWaveformVerification',
      passageVerificationRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to update a passage time stamp value.
  async updatePassageTimeStamp(
    updatePassageTimeStamp: UpdatePassageTimeStampRequest
  ) {
    return await apiClient.post(
      '/pigrun/UpdatePassageTimeStamp',
      updatePassageTimeStamp,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to create a download recorded passage data request.
  async downloadRecordedPassageDataRequest(
    downloadRecordedPassageDataRequest: DownloadRecordedPassageDataRequest
  ) {
    return await apiClient.post(
      '/pigrun/DownloadRecordedPassageDataRequest',
      downloadRecordedPassageDataRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to validate a download recorded passage data request.
  async validateDownloadRecordedPassageDataRequest(
    downloadRecordedPassageDataRequest: DownloadRecordedPassageDataRequest
  ) {
    return await apiClient.post(
      '/pigrun/ValidateDownloadRecordedPassageDataRequest',
      downloadRecordedPassageDataRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to end a download recorded passage data session.
  async endDownloadRecordedPassageDataSession(
    downloadRecordedPassageDataSessionId: number
  ) {
    return await apiClient.get(
      '/pigrun/EndDownloadRecordedPassageDataSession/' +
        downloadRecordedPassageDataSessionId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to create a download recorded raw data request.
  async downloadRecordedRawDataRequest(
    downloadRecordedRawDataRequest: DownloadRecordedRawDataRequest
  ) {
    return await apiClient.post(
      '/pigrun/DownloadRecordedRawDataRequest',
      downloadRecordedRawDataRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to get detail for an individual download recorded passage data request.
  async getDownloadRecordedPassageDataRequestDetail(
    downloadRecordedPassageDataSessionId: number
  ) {
    return await apiClient.get(
      '/pigrun/GetDownloadRecordedPassageDataRequestDetail/' +
        downloadRecordedPassageDataSessionId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to get CommandQueue detail for the specified cid value.
  async getCommandQueueDetail(cid: number) {
    return await apiClient.get('/pigrun/GetCommandQueueDetail/' + cid, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  // Web api service call used to perform a download recorded passage data firmware validation for the specified list of uid values.
  async validateDownloadRecordedPassageDataRequestFirmware(
    downloadRecordedPassageDataFirmwareValidationRequest: DownloadRecordedPassageDataFirmwareValidationRequest
  ) {
    return await apiClient.post(
      '/pigrun/ValidateDownloadRecordedPassageDataRequestFirmware',
      downloadRecordedPassageDataFirmwareValidationRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to cancel an in progress download recorded passage data session.
  async cancelDownloadRecordedPassageDataRequest(
    downloadRecordedPassageDataSessionId: number
  ) {
    return await apiClient.get(
      '/pigrun/CancelDownloadRecordedPassageDataRequest/' +
        downloadRecordedPassageDataSessionId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to get user detail.
  async getUser() {
    return await apiClient.get('/pigrun/user/get-user', {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async createPigRun(request: AddPigRunRequest) {
    return await apiClient.post('/pigrun/add', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async updatePigRun(request: UpdatePigRunRequest) {
    return await apiClient.put('/pigrun/update', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async getPipelineSectionMarkers(pipelineSectionId: number) {
    return await apiClient.get(
      `/pigrun/pipelinesectionmarker/${pipelineSectionId}`,
      {
        headers: { Authorization: `Bearer ${store.getters.token}` },
      }
    );
  },

  // Web api service call used to update application-to-person messaging state.
  async updateA2PMessagingState(
    updateA2PMessagingStateRequest: UpdateA2PMessagingStateRequest
  ) {
    return await apiClient.post(
      '/pigrun/user/update-a2p-messaging-state',
      updateA2PMessagingStateRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // Web api service call used to retrieve the machine learning result value of an aid.
  async getMachineLearningResultsRequest(
    machineLearningResultsRequest: MachineLearningResultsRequest
  ) {
    return await apiClient.post(
      '/pigrun/GetMachineLearningResults',
      machineLearningResultsRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  // Web api service call to retrieve all tool type records
  async getAllToolTypes() {
    return await apiClient.get('/pigrun/ToolType/GetAll', {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  // Web api service call to retrieve tool object
  async getTool(getToolRequest: GetToolRequest) {
    return await apiClient.post('/pigrun/Tool/GetTool', getToolRequest, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  // Web api service call to update the job number for a runId
  async updateJobNumber(
    updatePigRunJobNumberRequest: UpdatePigRunJobNumberRequest
  ) {
    return await apiClient.post(
      '/pigrun/UpdateJobNumber',
      updatePigRunJobNumberRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  // Web api service call to add a pigrun tool row to the PigRunTool table
  async addPigRunTool(addPigRunToolRequest: AddPigRunToolRequest) {
    return await apiClient.post('/pigrun/AddPigRunTool', addPigRunToolRequest, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  // Web api service call to add a pigrun tool row to the PigRunTool table
  async deleteAllPigRunToolsForPigRun(
    deleteAllPigRunToolsForPigRunRequest: DeleteAllPigRunToolsForPigRunRequest
  ) {
    return await apiClient.post(
      '/pigrun/DeleteAllPigRunTools',
      deleteAllPigRunToolsForPigRunRequest,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  // Web api service call to get a list of rows from PigRunTool from a runId.
  async getPigRunToolForRunId(runId: number) {
    return await apiClient.get('/pigrun/GetPigRunToolForRunId/' + runId, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  // Web api service call to get a list of rows from MachineLearningAlgorithmTool from a toolId.
  async getMachineLearningAlgorithmToolForToolId(toolId: number) {
    return await apiClient.get(
      '/pigrun/GetMachineLearningAlgorithmToolForToolId/' + toolId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // ----------------------------------------------------------------------------------------------------------------------------------------------

  addWebPassage(webPassage: WebPassage) {
    const stringifiedPassage: any = JSON.stringify(webPassage);

    return apiClient.post('/pigrun/addwebpassage', stringifiedPassage, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  // getActivePigRunListForUserWithNotification.
  //
  // This service method is used to get the list of active pig run id values for which the specified user has a notification subscription.
  //
  // From a functional perspective, this is part of the implementation where an authenticated user, with viewer role membership, is only
  // permitted to view the active pig runs they have a notification subscription for.
  getActivePigRunListForUserWithNotification(userId: string) {
    return apiClient.get(
      '/pigrun/ActivePigRunListWithUserNotification/' + userId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  // getActivePigRunUidListForUserWithNotification.
  //
  // This service method is used to get the list of uid values participating in active pig run(s) for which the specified user has a
  // notification subscription.
  //
  // From a functional perspective, this is part of the implementation where an authenticated user, with viewer role membership, is only
  // permitted to view the agm icons and corresponding data participating in active pig run(s) they have a notification subscription for.
  getActivePigRunUidListForUserWithNotification(userId: string) {
    return apiClient.get(
      '/pigrun/ActivePigRunUidListWithUserNotification/' + userId,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  getPigRuns() {
    return apiClient.get('/pigrun', {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  getActivePigRuns() {
    return apiClient.get('/pigrun/active', {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  getActivePigRun(pigRunId: number) {
    return apiClient.get(`/pigrun/active/${pigRunId}`, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  getDatabaseServerUtcDate() {
    return apiClient.get('/getdatabaseserverutcdate', {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  getPigRun(runId: number) {
    return apiClient.get('/pigrun/' + runId, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  cancelPigRun(runId: number) {
    return apiClient.put('/pigrun/cancel/' + runId, null, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  archivePigRun(pigRunId: number) {
    return apiClient.put('/pigrun/archive/' + pigRunId, null, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  synchFinishedPigRun(RunId: number) {
    const postBody: any = JSON.parse(JSON.stringify(RunId));
    return apiClient.post('/pigrun/finished/synch/', postBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  addMarkerPassage() {
    const postBody: any = {};
    return apiClient.post('/pigrun/markerpassage/add', postBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  async getMarkerPassages(pipelineSectionMarkerId: number) {
    return apiClient.get(
      `/pigrun/pipelinesectionmarkerpassages/${pipelineSectionMarkerId}`,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  async getPipelineSectionMarkerDetail(pipelineSectionMarkerId: number) {
    return apiClient.get(
      `/pigrun/pipelinesectionmarkerdetail/${pipelineSectionMarkerId}`,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  async hasVerifiedPassageAtMarker(pipelineSectionMarkerId: number) {
    return apiClient.get(
      `/pigrun/pipelinesectionmarker/${pipelineSectionMarkerId}/hasverifiedpassage`,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  async getVerifiedPassageData(pigRunId: number) {
    return apiClient.get(`/pigrun/${pigRunId}/verifiedpassagedata`, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  verifyMarkerPassage() {
    const postBody: any = {};
    return apiClient.post('/pigrun/markerpassage/verify', postBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  deleteMarkerPassage(runId: number, passageTimeStamp: string) {
    return apiClient.delete(
      '/pigrun/markerpassage/{runId}/{passageTimestamp}',
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  endPigRun(pigRunId: number, receiveDate: string) {
    return apiClient.post(
      '/pigrun/endRun',
      { pigRunId, receiveDate },
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },
  updateAgms(pigRun: PigRun) {
    const postBody: any = JSON.parse(JSON.stringify(pigRun));
    const allowed = ['runId', 'path'];
    const filteredBody = Object.keys(postBody)
      .filter((key) => allowed.includes(key))
      .reduce((obj: Record<string, any>, key) => {
        obj[key] = postBody[key];
        return obj;
      }, {});

    return apiClient.post('/pigrun/updateAgms', filteredBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  updateMarker() {
    const postBody: any = {};
    return apiClient.post('/pigrun/updateMarker', postBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  updateMarkers() {
    const postBody: any = {};
    return apiClient.post('/pigrun/updateMarkers', postBody, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async reconstructPigRun(request: ReconstructPigRunRequest) {
    return await apiClient.post('/pigrun/reconstruct', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async launchLiveTrackPigRun(request: LaunchLiveTrackPigRunRequest) {
    return await apiClient.post('/pigrun/launchlivetrack', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async updateMarkerTrackState(request: UpdateMarkerTrackStateRequest) {
    return await apiClient.post('/pigrun/UpdateMarkerTrackState', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  async updateMarkerNote(request: UpdateMarkerNoteRequest) {
    return await apiClient.post('/pigrun/UpdateMarkerNote', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  async updateMarkerDescription(request: UpdateMarkerDescriptionRequest) {
    return await apiClient.post('/pigrun/UpdateMarkerDescription', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  async getNotifications(pigRunId: number) {
    return await apiClient.get(`/pigrun/${pigRunId}/notifications`, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async saveNotifications(
    pigRunId: number,
    notifications: PigRunNotification[]
  ) {
    return await apiClient.put(
      `/pigrun/${pigRunId}/notifications`,
      notifications,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  async addDynamicMarker(request: AddDynamicMarkerRequest) {
    return await apiClient.post('/pigrun/update/addmarker', request, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },

  async updateLaunchDate(pigRunId: number, launchDate: string) {
    return await apiClient.put(
      '/pigrun/updateLaunchDate',
      { pigRunId, launchDate },
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  async updateDevices(pigRunId: number, uids: number[]) {
    return await apiClient.put(
      '/pigrun/updateDevices',
      { pigRunId, uids },
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  async addDevices(pigRunId: number, uids: number[]) {
    return await apiClient.post(
      '/pigrun/addDevices',
      { pigRunId, uids },
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
  },

  /**
   * Fetch all passages above the ML confidence threshold for the given pig
   * runs in a single round-trip.
   * Backend: POST /pigrun/mlpassages
   */
  async getMLPassages(
    request: IMLPassagesRequest
  ): Promise<IMLPassageResult[]> {
    const response = await apiClient.post<IMLPassageResult[]>(
      '/pigrun/mlpassages',
      request,
      { headers: { Authorization: `Bearer ${store.getters.token}` } }
    );
    return response.data;
  },
};
